'use client'

import { Fragment, useState } from 'react'
import type { MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { HmrcTokenExpiredError, useHMRCITSAStore } from '@repo/store/useHMRCITSAStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { isValidNI } from '@repo/validation/isValidNI'
import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { NINumberField } from '../NINumberField/NINumberField'
import { SubmissionStatusPill } from '../SubmissionStatusPill/SubmissionStatusPill'
import type { SubmissionStatus, SubmissionType } from '../types'
import MUIAlert from '@mui/material/Alert'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUICollapse from '@mui/material/Collapse'
import MUIMenu from '@mui/material/Menu'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITable from '@mui/material/Table'
import MUITableBody from '@mui/material/TableBody'
import MUITableCell from '@mui/material/TableCell'
import MUITableContainer from '@mui/material/TableContainer'
import MUITableHead from '@mui/material/TableHead'
import MUITableRow from '@mui/material/TableRow'
import MUITypography from '@mui/material/Typography'

// Shape returned by HmrcMtdItsaSubmission/GetAllByNationalInsuranceNumber (BE2) — camelCase
// per the JSON contract (see useHMRCITSAStore). periodStartDate/periodEndDate are nullable
// (an AnnualSummary carries no quarterly period); receiptReference is nullable (FR-09 — a
// failed submit may not carry an HMRC receipt/correlation id). hmrcRequestPayload/
// hmrcResponsePayload are JSON strings, rendered verbatim in the expandable audit panel.
export type SubmissionHistoryRow = {
  hmrcMtdItsaSubmissionId: string | number
  submittedDate: string
  businessId: string
  taxYear: string
  periodStartDate?: string | null
  periodEndDate?: string | null
  submissionType: SubmissionType
  status: SubmissionStatus
  receiptReference?: string | null
  hmrcRequestPayload?: string | null
  hmrcResponsePayload?: string | null
}

const SUBMISSION_TYPE_LABELS: Record<SubmissionType, string> = {
  CumulativePeriodSummary: 'Quarterly update',
  AnnualSummary: 'Annual summary'
}

// The quarterly submission screen (FE7a/FE7b) and the annual summary screen (FE8) — both
// reached ONLY from this landing screen's New Submission menu (trial-review consolidation:
// they carry no navigation entries of their own in MenuStructure.jsx).
export const NEW_SUBMISSION_ROUTE_PATH = '/reports/itsa-mtd-quarterly'
export const ANNUAL_SUBMISSION_ROUTE_PATH = '/reports/itsa-mtd-annual'

// The HMRC OAuth setup screen (FE5) — same path + navigation-state contract SetupModule
// itself expects (SetupModule.tsx reads location.state.tokenExpired), mirroring the
// johntaylor prototype's handleTokenExpired navigate call.
export const HMRC_SETUP_ROUTE_PATH = '/supervisor/financial-setup/hmrc-itsa-setup'

const GENERIC_LOAD_ERROR = 'An error occurred while loading submissions. Please try again.'

const formatDate = (value?: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-GB')
}

const formatSubmittedDate = (value: string): string =>
  new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })

const formatPeriod = (row: SubmissionHistoryRow): string => {
  if (!row.periodStartDate || !row.periodEndDate) return '—'
  return `${formatDate(row.periodStartDate)} – ${formatDate(row.periodEndDate)}`
}

// Renders a JSON-string payload pretty-printed, falling back to the raw string if it
// somehow isn't valid JSON (the stored payloads are always JSON per BE2, but this is a
// read-only audit view — never let a malformed row crash the screen).
const formatPayload = (payload: string): string => {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2)
  } catch {
    return payload
  }
}

const payloadBoxSx = {
  fontSize: '0.75rem',
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  backgroundColor: 'white',
  border: '1px solid',
  borderColor: 'lines.slate300',
  borderRadius: '4px',
  padding: 2,
  margin: 0,
  maxHeight: 300,
  overflowY: 'auto'
}

export const ReportModule = () => {
  const { getSubmissionHistory } = useHMRCITSAStore()
  // Estate pattern (see CaseTypeMappingModule): on reduced-height windows the layout hides
  // the PageTitleBlock (and with it the flow offset that clears the FIXED breadcrumb bar),
  // so the module must supply its own top clearance or render underneath the breadcrumb.
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const navigate = useNavigate()

  const [nationalInsuranceNumber, setNationalInsuranceNumber] = useState('')
  // null = nothing loaded yet (or the last load failed) — deliberately distinct from `[]`
  // (a genuine zero-submissions result) so the table is never rendered as a stand-in for an
  // error state.
  const [rows, setRows] = useState<SubmissionHistoryRow[] | null>(null)
  const [expandedSubmissionId, setExpandedSubmissionId] = useState<string | number | null>(null)
  const [loading, setLoading] = useState(false)
  const [tokenExpired, setTokenExpired] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [newSubmissionAnchorEl, setNewSubmissionAnchorEl] = useState<null | HTMLElement>(null)

  const isValidNumber = isValidNI(nationalInsuranceNumber)

  const handleViewSubmissions = async () => {
    setTokenExpired(false)
    setLoadError('')
    setExpandedSubmissionId(null)
    setLoading(true)
    try {
      const result = await getSubmissionHistory({ nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase() })
      setRows((result as SubmissionHistoryRow[]) || [])
    } catch (error) {
      // CRITICAL: this must never be treated as "no submissions" — a token-expired error
      // surfaces a re-link prompt, any other error surfaces its own message. Neither case
      // renders the table (no `.catch(() => [])`).
      setRows(null)
      if (error instanceof HmrcTokenExpiredError) {
        setTokenExpired(true)
      } else {
        setLoadError((error as Error)?.message || GENERIC_LOAD_ERROR)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReconnect = () => {
    navigate(HMRC_SETUP_ROUTE_PATH, { state: { tokenExpired: true } })
  }

  const handleNewSubmissionClick = (event: MouseEvent<HTMLElement>) => {
    setNewSubmissionAnchorEl(event.currentTarget)
  }

  const handleNewSubmissionClose = () => {
    setNewSubmissionAnchorEl(null)
  }

  const handleNewQuarterlySubmission = () => {
    setNewSubmissionAnchorEl(null)
    navigate(NEW_SUBMISSION_ROUTE_PATH)
  }

  const handleNewAnnualSubmission = () => {
    setNewSubmissionAnchorEl(null)
    navigate(ANNUAL_SUBMISSION_ROUTE_PATH)
  }

  const toggleRow = (hmrcMtdItsaSubmissionId: string | number) =>
    setExpandedSubmissionId((prev) => (prev === hmrcMtdItsaSubmissionId ? null : hmrcMtdItsaSubmissionId))

  const showTable = rows !== null && !tokenExpired && !loadError

  return (
    <MUIStack
      data-testid="ReportModule"
      gap={3}
      sx={{
        maxWidth: 1100,
        margin: 'auto',
        mt: pageTitleBlockIsVisible ? 'auto' : '12rem',
        backgroundColor: 'white',
        borderRadius: '6px',
        padding: '40px'
      }}
    >
      <MUIStack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <MUIStack gap={1}>
          <MUITypography variant="h5">HMRC MTD ITSA submissions</MUITypography>
          <MUITypography variant="body1">
            {`Enter a National Insurance Number to view all HMRC MTD ITSA submissions recorded for that individual. Select a row to view the stored HMRC request and response payloads.`}
          </MUITypography>
        </MUIStack>
        <MUIButton
          data-testid="ReportModule-newSubmissionButton"
          variant="contained"
          color="primary"
          onClick={handleNewSubmissionClick}
        >
          New Submission
        </MUIButton>
        <MUIMenu
          data-testid="ReportModule-newSubmissionMenu"
          anchorEl={newSubmissionAnchorEl}
          open={Boolean(newSubmissionAnchorEl)}
          onClose={handleNewSubmissionClose}
        >
          <MUIMenuItem data-testid="ReportModule-newQuarterlySubmissionOption" onClick={handleNewQuarterlySubmission}>
            Quarterly submission
          </MUIMenuItem>
          <MUIMenuItem data-testid="ReportModule-newAnnualSubmissionOption" onClick={handleNewAnnualSubmission}>
            Annual submission
          </MUIMenuItem>
        </MUIMenu>
      </MUIStack>

      <MUIAlert
        severity="info"
        data-testid="ReportModuleLiabilityEstimateNotice"
      >
        View your estimated Income Tax liability in your HMRC online account:{' '}
        <a href="https://www.gov.uk/sign-in-hmrc-online-services" data-testid="ReportModuleLiabilityEstimateLink">
          https://www.gov.uk/sign-in-hmrc-online-services
        </a>
        . Osprey submits your self-employment figures; HMRC calculates your estimate as it receives them.
      </MUIAlert>

      <MUIAlert
        severity="info"
        data-testid="ReportModuleCombinationSoftwareNotice"
      >
        Osprey covers your law-practice self-employment source. Other income sources, loss claims and your final
        declaration are completed in compatible tax software or by your accountant.
      </MUIAlert>

      {tokenExpired && (
        <MUIAlert
          severity="warning"
          data-testid="ReportModuleTokenExpiredBanner"
          action={
            <MUIButton color="inherit" size="small" data-testid="ReportModuleReconnectButton" onClick={handleReconnect}>
              Reconnect to HMRC
            </MUIButton>
          }
        >
          Your HMRC access has expired. Please reconnect your account to view submissions.
        </MUIAlert>
      )}

      {!tokenExpired && loadError && (
        <MUIAlert severity="error" data-testid="ReportModuleLoadError">
          {loadError}
        </MUIAlert>
      )}

      <MUIStack direction="row" gap={2} alignItems="flex-start">
        <NINumberField value={nationalInsuranceNumber} onChange={setNationalInsuranceNumber} />
        <MUIButton
          data-testid="ReportModule-viewSubmissionsButton"
          variant="outlined"
          onClick={handleViewSubmissions}
          disabled={!isValidNumber || loading}
          sx={{ mt: '4px' }}
        >
          {loading ? <MUICircularProgress size={20} /> : 'View submissions'}
        </MUIButton>
      </MUIStack>

      {showTable && rows.length === 0 && (
        <MUITypography variant="body2" data-testid="ReportModuleEmptyState">
          No submissions found for this National Insurance Number.
        </MUITypography>
      )}

      {showTable && rows.length > 0 && (
        <MUIPaper data-testid="ReportModuleGrid" sx={{ width: '100%' }}>
          <MUITableContainer>
            <MUITable size="small">
              <MUITableHead>
                <MUITableRow>
                  <MUITableCell sx={{ width: 32 }} />
                  <MUITableCell>Submitted</MUITableCell>
                  <MUITableCell>Type</MUITableCell>
                  <MUITableCell>Tax year</MUITableCell>
                  <MUITableCell>Period</MUITableCell>
                  <MUITableCell>Receipt reference</MUITableCell>
                  <MUITableCell>Status</MUITableCell>
                </MUITableRow>
              </MUITableHead>
              <MUITableBody>
                {rows.map((submission) => {
                  const isExpanded = expandedSubmissionId === submission.hmrcMtdItsaSubmissionId

                  return (
                    <Fragment key={submission.hmrcMtdItsaSubmissionId}>
                      <MUITableRow
                        hover
                        data-testid="ReportModuleHistoryRow"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => toggleRow(submission.hmrcMtdItsaSubmissionId)}
                      >
                        <MUITableCell sx={{ width: 32 }}>
                          <ChevronRightIcon
                            sx={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
                          />
                        </MUITableCell>
                        <MUITableCell>{formatSubmittedDate(submission.submittedDate)}</MUITableCell>
                        <MUITableCell>
                          {SUBMISSION_TYPE_LABELS[submission.submissionType] || submission.submissionType}
                        </MUITableCell>
                        <MUITableCell>{submission.taxYear}</MUITableCell>
                        <MUITableCell>{formatPeriod(submission)}</MUITableCell>
                        <MUITableCell>{submission.receiptReference || '—'}</MUITableCell>
                        <MUITableCell>
                          <SubmissionStatusPill status={submission.status} />
                        </MUITableCell>
                      </MUITableRow>
                      <MUITableRow>
                        <MUITableCell colSpan={7} sx={{ p: 0, border: isExpanded ? undefined : 'none' }}>
                          <MUICollapse in={isExpanded} timeout="auto" unmountOnExit>
                            <MUIStack
                              data-testid="ReportModuleHistoryPayloads"
                              gap={2}
                              sx={{ p: 2, backgroundColor: 'background.mid' }}
                            >
                              <MUIStack gap={1}>
                                <MUITypography variant="subtitle2">HMRC request</MUITypography>
                                <MUIBox component="pre" sx={payloadBoxSx} data-testid="ReportModuleRequestPayload">
                                  {formatPayload(submission.hmrcRequestPayload || '')}
                                </MUIBox>
                              </MUIStack>
                              <MUIStack gap={1}>
                                <MUITypography variant="subtitle2">HMRC response</MUITypography>
                                <MUIBox component="pre" sx={payloadBoxSx} data-testid="ReportModuleResponsePayload">
                                  {formatPayload(submission.hmrcResponsePayload || '')}
                                </MUIBox>
                              </MUIStack>
                            </MUIStack>
                          </MUICollapse>
                        </MUITableCell>
                      </MUITableRow>
                    </Fragment>
                  )
                })}
              </MUITableBody>
            </MUITable>
          </MUITableContainer>
        </MUIPaper>
      )}
    </MUIStack>
  )
}
